import { Chip, InputAdornment, OutlinedInput, Stack } from "@mui/material";
import React from "react";
import { GridRowsProp, GridColDef } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { setupService } from "../../../../Core/Api/services";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import SearchIcon from "@mui/icons-material/Search";
import { DbCultureInfos } from "../../../../Core/Api/Types/db-culture-infos";
import { t } from "i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsSystemCultures() {
    // Table filter
    const [textFilter, setTextFilter] = React.useState("");
    // Loading
    const [isLoading, setIsLoading] = React.useState(true);

    // Table rows
    const [rows, setRows] = React.useState<DbCultureInfos[]>([]);

    // #region Grid definition
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const cols: GridColDef[] = React.useMemo(
        () => [
            {
                headerName: t("table:headerName.englishName"),
                field: "englishName",
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: Row) => data.value,
            },
            {
                headerName: t("table:headerName.displayName"),
                field: "displayName",
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: Row) => data.value,
            },
            {
                headerName: t("table:headerName.isoLetters", { count: 2 }),
                field: "isoCodeTwoLetters",
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                renderCell: (data: Row) => data.value,
            },
            {
                headerName: t("table:headerName.isoLetters", { count: 3 }),
                field: "isoCodeThreeLetters",
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                renderCell: (data: Row) => data.value,
            },
            {
                headerName: t("table:headerName.languagesLocalized"),
                field: "languages",
                flex: CellSize.large,
                minWidth: CellSize.large,
                valugetCellValue: (params) => {
                    return params?.row?.languages?.map((lang) => lang.isoCodeLocalized).join(", ");
                },
                renderCell: (data: Row) => (
                    <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
                        {data.value.slice(0, 10).map((v, index) => (
                            <Chip
                                label={v.isoCodeLocalized}
                                size="small"
                                sx={{ mb: 0.5 }}
                                key={`${data?.row?.id}-${index}`}
                                color={v.isMain ? "primary" : "default"}
                            />
                        ))}
                        {data.value.length > 10 && (
                            <Chip label={`+${data.value.length - 10}`} size="small" sx={{ mb: 0.5 }} key={`${data?.row?.id}-plus`} />
                        )}
                    </Stack>
                ),
            },
            {
                headerName: t("table:headerName.languagesMappedToInternal"),
                field: "languagesMap",
                flex: CellSize.large,
                minWidth: CellSize.large,
                valugetCellValue: (params) => {
                    return params?.row?.languagesMap?.map((lang) => lang.value).join(", ");
                },
                renderCell: (data: Row) => (
                    <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
                        {data.value.slice(0, 10).map((v, index) => (
                            <Chip label={v.value} size="small" sx={{ mb: 0.5 }} key={`${data?.row?.id}-${index}-map`} />
                        ))}
                        {data.value.length > 10 && (
                            <Chip
                                label={`+${data.value.length - 10}`}
                                size="small"
                                sx={{ mb: 0.5 }}
                                key={`${data?.row?.id}-plus-map`}
                            />
                        )}
                    </Stack>
                ),
            },
        ],
        []
    );
    // #endregion

    React.useEffect(() => {
        setIsLoading(true);
        setupService.GetAllCultures().then(function (response) {
            if (response.success) setRows(response.cultures ?? []);
            setIsLoading(false);
        });
    }, []);

    return (
        <DataTable
            loading={isLoading}
            tableKey={TableUuids.SETTINGS_SYSTEM_CULTURES}
            columns={cols}
            rows={rows}
            rowHeight={64}
            getRowHeight={() => "auto"}
            pagination={true}
            disableRowSelectionOnClick
        />
    );
}

export default AdminSettingsSystemCultures;
